import { useTranslation } from 'react-i18next';

import { TaskList } from '../../../../../components/design-system/analytics';
import { RelativeTime } from '../../../../../components/design-system/admin';
import type { Lead } from '../../../../../common/hooks/use-leads-api.hook';
import type { LeadReading } from '../lead-temperature';
import { VIZ_LINKS } from '../links';

/** Past the list: the waiting ones if there are more of them, otherwise every lead. */
const hiddenOrAll = (waiting: number) => (waiting > 5 ? VIZ_LINKS.leadsWaiting : VIZ_LINKS.leads);

/**
 * The people to write to today: hot and warm leads nobody has emailed yet,
 * hottest first, each with a way straight to the composer.
 */
export function LeadsWaiting({
  leads,
  readings,
  loading,
  className,
}: {
  leads: Lead[];
  readings: Map<string, LeadReading>;
  loading?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const waiting = leads.filter((l) => {
    const r = readings.get(l._id);
    return r && (r.action === 'email_now' || r.action === 'follow_up' || r.action === 'reply');
  });

  return (
    <TaskList
      title={t('VIZ_ANALYTICS.LEADS_WAITING.TITLE')}
      description={t('VIZ_ANALYTICS.LEADS_WAITING.DESCRIPTION')}
      loading={loading}
      max={5}
      testId="viz-leads-waiting"
      emptyTitle={t('VIZ_ANALYTICS.LEADS_WAITING.EMPTY_TITLE')}
      emptyText={t('VIZ_ANALYTICS.LEADS_WAITING.EMPTY_TEXT')}
      more={{
        label: (hidden) => (hidden > 0 ? t('VIZ_ANALYTICS.LEADS_WAITING.MORE', { count: hidden }) : t('VIZ_ANALYTICS.LEADS_WAITING.ALL')),
        href: hiddenOrAll(waiting.length),
        always: true,
      }}
      items={waiting.map((lead) => {
        const r = readings.get(lead._id)!;
        return {
          id: lead._id,
          tone: r.temperature === 'hot' ? 'bad' : 'watch',
          title: lead.email,
          detail: (
            <>
              {t(`VIZ_ANALYTICS.TEMPERATURE.${r.temperature.toUpperCase()}`)} ·{' '}
              {t('VIZ_ANALYTICS.LEADS.DOWNLOADS', { count: lead.downloadCount ?? 0 })} · <RelativeTime value={lead.lastSeenAt} />
            </>
          ),
          action: { label: t(`VIZ_ANALYTICS.LEADS.ACTION.${r.action.toUpperCase()}`), href: VIZ_LINKS.compose(lead.email) },
        };
      })}
    />
  );
}
